import * as React from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { useUpsertAddOn } from "@/hooks/useAdmin";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import type { AddOnDto, UpsertAddOnRequest } from "@/types/api";

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  initial?: AddOnDto;
}

const EMPTY: UpsertAddOnRequest = {
  slug: "", name: "", description: "",
  scope: "Container",
  price_monthly: 0, is_one_time: false, hostshop_product_code: "",
  mailboxes_increment: 0, bulk_emails_increment: 0, aliases_increment: 0, forwarders_increment: 0,
  storage_gb_increment: 0, websites_increment: 0, smtp_relays_increment: 0, ssl_certs_increment: 0,
  active: true
};

export function AddOnEditorDialog({ open, onOpenChange, initial }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = React.useState<UpsertAddOnRequest>(EMPTY);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string[]>>({});
  const upsert = useUpsertAddOn();
  const { toast } = useToast();

  React.useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    setForm(initial ? fromAddOn(initial) : EMPTY);
  }, [open, initial]);

  function patch<K extends keyof UpsertAddOnRequest>(key: K, value: UpsertAddOnRequest[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});
    try {
      await upsert.mutateAsync(form);
      toast({ title: isEdit ? "Add-on updated" : "Add-on created", variant: "success" });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.body && typeof err.body === "object") {
        const body = err.body as { errors?: Record<string, string[]>; message?: string };
        if (body.errors) setFieldErrors(body.errors);
        toast({ title: "Validation failed", description: body.message ?? err.message, variant: "destructive" });
      } else {
        toast({ title: "Save failed", description: err instanceof Error ? err.message : "Network error.", variant: "destructive" });
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${initial?.name}` : "New add-on"}</DialogTitle>
          <DialogDescription>
            Container-scoped add-ons attach to one domain. Account-scoped extend the pool every domain shares.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="slug" label="Slug" value={form.slug} onChange={v => patch("slug", v)} disabled={isEdit} errors={fieldErrors["Request.Slug"]} placeholder="mailbox-5" />
            <Field id="name" label="Name" value={form.name} onChange={v => patch("name", v)} errors={fieldErrors["Request.Name"]} />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <SelectField id="scope" label="Scope" value={form.scope}
              options={[{ value: "Container", label: "Container (per domain)" }, { value: "Account", label: "Account-wide" }]}
              onChange={v => patch("scope", v as "Container" | "Account")}
              errors={fieldErrors["Request.Scope"]} />
            <Field id="price_monthly" type="number" label="Price /mo" value={String(form.price_monthly)} onChange={v => patch("price_monthly", nd(v))} />
            <CheckboxField id="is_one_time" label="One-time" checked={form.is_one_time} onChange={v => patch("is_one_time", v)} />
          </div>

          <TextAreaField id="description" label="Description" value={form.description ?? ""} onChange={v => patch("description", v)} />

          <Field id="hostshop_product_code" label="HostShop product code" value={form.hostshop_product_code ?? ""} onChange={v => patch("hostshop_product_code", v)} placeholder="custom:addon_mb5:" />

          <fieldset className="space-y-3 rounded-lg border border-border p-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Increments</legend>
            <div className="grid gap-3 sm:grid-cols-4">
              <Field id="mailboxes_increment"   type="number" label="Mailboxes"    value={String(form.mailboxes_increment)}   onChange={v => patch("mailboxes_increment",   n(v))} />
              <Field id="bulk_emails_increment" type="number" label="Bulk emails"  value={String(form.bulk_emails_increment)} onChange={v => patch("bulk_emails_increment", n(v))} />
              <Field id="aliases_increment"     type="number" label="Aliases"      value={String(form.aliases_increment)}     onChange={v => patch("aliases_increment",     n(v))} />
              <Field id="forwarders_increment"  type="number" label="Forwarders"   value={String(form.forwarders_increment)}  onChange={v => patch("forwarders_increment",  n(v))} />
              <Field id="storage_gb_increment"  type="number" label="Storage GB"   value={String(form.storage_gb_increment)}  onChange={v => patch("storage_gb_increment",  nd(v))} />
              <Field id="websites_increment"    type="number" label="Websites"     value={String(form.websites_increment)}    onChange={v => patch("websites_increment",    n(v))} />
              <Field id="smtp_relays_increment" type="number" label="SMTP relays"  value={String(form.smtp_relays_increment)} onChange={v => patch("smtp_relays_increment", n(v))} />
              <Field id="ssl_certs_increment"   type="number" label="SSL certs"    value={String(form.ssl_certs_increment)}   onChange={v => patch("ssl_certs_increment",   n(v))} />
            </div>
          </fieldset>

          <CheckboxField id="active" label="Active (shown on /add-ons)" checked={form.active} onChange={v => patch("active", v)} />

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={upsert.isPending}>Cancel</Button>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Create add-on"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface FieldProps {
  id: string; label: string; value: string; onChange: (next: string) => void;
  type?: string; disabled?: boolean; placeholder?: string; errors?: string[];
}
function Field({ id, label, value, onChange, type, disabled, placeholder, errors }: FieldProps) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type ?? "text"} value={value} onChange={e => onChange(e.target.value)}
             disabled={disabled} placeholder={placeholder} aria-invalid={!!errors?.length} />
      {errors?.length ? <p className="text-xs text-destructive">{errors[0]}</p> : null}
    </div>
  );
}

function SelectField({ id, label, value, options, onChange, errors }: {
  id: string; label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void; errors?: string[];
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <select id={id} value={value} onChange={e => onChange(e.target.value)}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-invalid={!!errors?.length}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {errors?.length ? <p className="text-xs text-destructive">{errors[0]}</p> : null}
    </div>
  );
}

function TextAreaField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <textarea id={id} rows={2} value={value} onChange={e => onChange(e.target.value)}
        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
    </div>
  );
}

function CheckboxField({ id, label, checked, onChange }: { id: string; label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label htmlFor={id} className="flex items-end gap-2 text-sm">
      <input id={id} type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
      <span>{label}</span>
    </label>
  );
}

const n  = (s: string) => { const v = parseInt(s, 10);   return Number.isFinite(v) ? v : 0; };
const nd = (s: string) => { const v = parseFloat(s);     return Number.isFinite(v) ? v : 0; };

function fromAddOn(a: AddOnDto): UpsertAddOnRequest {
  return {
    slug: a.slug, name: a.name, description: a.description ?? "",
    scope: (a.scope === "Account" ? "Account" : "Container"),
    price_monthly: a.price_monthly, is_one_time: a.is_one_time, hostshop_product_code: a.hostshop_product_code ?? "",
    mailboxes_increment: a.mailboxes_increment, bulk_emails_increment: a.bulk_emails_increment,
    aliases_increment: a.aliases_increment,     forwarders_increment: a.forwarders_increment,
    storage_gb_increment: a.storage_gb_increment, websites_increment: a.websites_increment,
    smtp_relays_increment: a.smtp_relays_increment, ssl_certs_increment: a.ssl_certs_increment,
    active: a.active
  };
}
